import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to add ESLint to your project",
  "description": "",
  "published": "2016-08-31T00:00:00.000Z",
  "redirect_from": ["/blog/how-to-add-eslint-to-your-project/swizec/6990"],
  "categories": "Front End, linting, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Yesterday, I wrote about how fun it was to `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/i-added-linting-to-a-3-year-old-project-you-can-totally-guess-what-happened-next/swizec/6986"
      }}>{`add linting to a 3 year old project`}</a>{`, discover 856 errors, give up immediately, and downgrade all errors to warnings so I can sleep at night.`}</p>
    <p>{`2% error rate per line of code – 2 lines with problems for every 100 lines of code – is disheartening as hell. But you know what? You can’t fix a problem you don’t know you have.`}</p>
    <p>{`By the way, here’s how you count them:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`> webpack --config=webpack.config.js | grep problems > problems.txt
`}</code></pre>
    <p>{`This gives you a file full of lines like `}<inlineCode parentName="p">{`✖ 18 problems (0 errors, 18 warnings)`}</inlineCode>{`. One line for each file with linter issues. 175 of those in my case.`}</p>
    <p>{`If your Bash is as bad as mine, you can count the problems with one line of python. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`// throwaway.py
import re

print sum(int(re.search('\\d+', line).group(0)) for line in open('problems.txt'))
`}</code></pre>
    <p>{`Iterate file line-by-line, extract first number of each line, turn it into an integer, sum. Isn’t Python great? This would be hell to do in JavaScript.`}</p>
    <p>{`In my case, this prints 856. I hope you get less!`}</p>
    <p>{`So, how do you get that evil mean output that says your code’s no good when running Webpack? There are 3 steps:`}</p>
    <ol>
      <li parentName="ol">{`Install some npm packages`}</li>
      <li parentName="ol">{`Add 5 lines to Webpack`}</li>
      <li parentName="ol">{`Get a sensible `}<inlineCode parentName="li">{`.eslintrc`}</inlineCode>{` config file`}</li>
    </ol>
    <h2 {...{
      "id": "1-npm-packages"
    }}>{`1. npm packages`}</h2>
    <p>{`It’s 2016, so I’m going to assume you’re using ES6 to write your code. Even if you aren’t, empowering ESLint to understand modern JavaScript can’t hurt.`}</p>
    <p>{`The packages you need are: eslint, babel-eslint, eslint-loader.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`> npm install --save-dev eslint babel-eslint eslint-loader
`}</code></pre>
    <p>{`This installs the packages and saves them as `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` in your `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file. If you’re using Heroku, you have to set them as normal dependencies with `}<inlineCode parentName="p">{`--save`}</inlineCode>{`. Otherwise your Webpack build will fail when deploying to Heroku because Heroku doesn’t install dev dependencies.`}</p>
    <p>{`That’s always fun to re-discover.`}</p>
    <h2 {...{
      "id": "2-5-lines-of-webpack-config"
    }}>{`2. 5 lines of Webpack config`}</h2>
    <p>{`Let’s assume you’re already using Webpack and have a config going. To add ESLint to your build step, add these lines to that config:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// webpack.config.js

module: {
    loaders: [
        // ...
        {
            test: /\\.js$/,
            include: [
                path.resolve(__dirname, PATH_TO_YOUR_CODE)
            ],
            loader: 'eslint',
            exclude: /node_modules/
        },
        // ...
}
`}</code></pre>
    <p>{`For every file in `}<inlineCode parentName="p">{`include`}</inlineCode>{` paths that ends with `}<inlineCode parentName="p">{`.js`}</inlineCode>{`, use the `}<inlineCode parentName="p">{`eslint`}</inlineCode>{` loader. The `}<inlineCode parentName="p">{`exclude`}</inlineCode>{` setting might be unnecessary, but I like to add it out of habit.`}</p>
    <p>{`Yes, the best place to put this is in `}<inlineCode parentName="p">{`loaders`}</inlineCode>{`. Not `}<inlineCode parentName="p">{`preLoaders`}</inlineCode>{` and not `}<inlineCode parentName="p">{`postLoaders`}</inlineCode>{`.`}</p>
    <p>{`Logically speaking, it fits best in `}<inlineCode parentName="p">{`preLoaders`}</inlineCode>{`, doesn’t it? You’d want to run the linter before doing any other transformation. That’s why it can’t go in `}<inlineCode parentName="p">{`postLoaders`}</inlineCode>{`.`}</p>
    <p>{`That caused strange errors for me that took … `}<em parentName="p">{`cough`}</em>{` … hours to figure out. When you use the bang syntax to specify loaders in `}<inlineCode parentName="p">{`require()`}</inlineCode>{` calls, `}<inlineCode parentName="p">{`eslint`}</inlineCode>{` gets confused and constructs file paths that do not exist.`}</p>
    <p>{`The easiest solution is to use it as a part of normal loaders. Ordering matters.`}</p>
    <p>{`If I switch places and put `}<inlineCode parentName="p">{`eslint`}</inlineCode>{` before `}<inlineCode parentName="p">{`babel-loader`}</inlineCode>{`, Webpack spits out 883 errors in 178 files. 27 more errors in 3 more files.`}</p>
    <p>{`I will pretend I didn’t see that.`}</p>
    <h2 {...{
      "id": "3-a-sensible-eslint-config"
    }}>{`3. A sensible ESLint config`}</h2>
    <p>{`Now, the fun part -> ESLint config in `}<inlineCode parentName="p">{`.eslintrc`}</inlineCode>{`. There are many files out there with varying degrees of annoyingness.`}</p>
    <p>{`You can use `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/eslint-config-airbnb"
      }}>{`AirBnB’s ESLint config`}</a>{`, which I’ve heard is annoying and makes you feel dirty.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I tried the airbnb ruleset. Threw so many errors I just disabled it. I felt so dirty...`}</p>{`— Jonas (@JonasBadalic) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/JonasBadalic/status/769777611434258432"
      }}>{`August 28, 2016`}</a></blockquote>
    <p>{`Then there’s `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/eslint-config-google"
      }}>{`Google’s ESLint config`}</a>{`, which I haven’t heard anything about. It does have a lot of downloads though. ¯`}<em parentName="p">{`(ツ)`}</em>{`/¯`}</p>
    <p>{`Or the option that would make me feel all warm and fuzzy: `}<a parentName="p" {...{
        "href": "https://gist.github.com/Swizec/7fb9ed92b4b56584d74de8dfcfd1c7d0"
      }}>{`my battle untested base config`}</a>{`. It’s designed to annoy you, but not too much. It’s based off of `}<a parentName="p" {...{
        "href": "https://github.com/codeclimate/codeclimate/blob/master/config/eslint/.eslintrc"
      }}>{`Code Climate’s config`}</a>{` with all errors turned into warnings.`}</p>
    <p>{`My favorite feature is that it lists all available options, so in theory it’s easy to fine-tune. I can already tell that we’ll have to crank up some of the styling rules and tune down some of the “potential error” rules.`}</p>
    <p>{`Now… how do I get team buy-in? ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      